import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { dateFormats } from '../../../../constants';
import { fullName } from '../../../../services';
import { adminActions } from '../../../../state/modules/admin';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream } from '../../../../utils';
import { GeneralTable, Icon, SearchForm, LoadingButton } from '../../../components';

const defaultTake = 12;

const defaultGetContentCompletionFilter = {
  skip: 0,
  take: defaultTake,
  tags: '',
};

const columns = [
  {
    key: 'content',
    name: 'Content'
  },
  {
    key: 'completions',
    name: 'User Completions'
  },
  {
    key: 'options',
    name: 'Options'
  }
];

const ContentCompletionAdminDashboard = ({
  query,
  setQuery,
  rows,
  hasMoreToLoad,
  loadMoreContent,
  isFetching,
}) => (
  <div className='base-layout'>
    <section className='layout--header'>
      <Grid>
        <Row className='button-gutter'>
          <Col xs={12}>
            <h1>Content Completion</h1>
          </Col>
        </Row>
      </Grid>
    </section>
    <section id='section--admin-content-completion' className='layout--section'>
      <Grid>
        <Row className='no-gutter sectionHeader'>
          <Col md={3} sm={12}>
            <SearchForm label="Search Content" searchQuery={query} onSearchChange={setQuery} />
          </Col>
        </Row>
        <Row className='no-gutter sectionHeader'>
          <Col sm={12}>
            <GeneralTable className='admin-task-group-table' rows={rows} columns={columns} />
          </Col>
        </Row>
        <Row className='no-gutter'>
          <Col>
            {
              hasMoreToLoad ?
                <LoadingButton text='Load More +' onClick={loadMoreContent} loading={isFetching} /> :
                null
            }
          </Col>
        </Row>
      </Grid>
    </section>
  </div >
);

const mapStateToProps = ({ admin }) => ({
  contentCompletionResult: admin.contentCompletion,
  isFetching: admin.isFetchingContentCompletion
});

const mapDispatchToProps = (dispatch) => ({
  getContentCompletion: bindActionCreators(adminActions.admin.getContentCompletion, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    {
      query: '',
      searchDebounce: undefined,
      filter: defaultGetContentCompletionFilter,
    },
    {
      setSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      setQuery: () => (e) => ({
        query: e.target.value
      }),
      setFilter: () => (nextFilter) => ({
        filter: nextFilter
      })
    }
  ),
  withHandlers({
    searchContent: ({ filter, getContentCompletion }) => () => {
      getContentCompletion(filter)
    },
    loadMoreContent: ({ filter, setFilter }) => () => {
      setFilter({
        ...filter,
        skip: filter.skip + defaultTake,
      })
    },
    exportContentCompletions: () => (contentName, csvRows) => {
      const csvColumns = ['User', 'Time Earned', 'Attempts'];

      downloadBlobStreamFile(
        writeDataToCsvStream(
          getCsvStream({
            header: true,
            columns: csvColumns,
          }),
          csvRows,
        ).pipe(getBlobStream()),
        `${contentName} user completions.csv`
      );
    }
  }),
  withProps(({ contentCompletionResult }) => ({
    contentCompletions: contentCompletionResult !== undefined 
      ? contentCompletionResult.contentCompletions 
      : [],
    contentHits: contentCompletionResult !== undefined 
    ? contentCompletionResult.hits 
    : 0,
  })),
  withProps(
    ({ contentCompletions, contentHits, exportContentCompletions }) => {
      const hasMoreToLoad = contentCompletions.length < contentHits;
      const rows = fp.map(
        ({ content, completions }) => {
          const contentName = he.decode(content.title);
          const data = fp.map(
            ({ user, date, attempts }) => [fullName(user), dateFnsCompat.format(dateFnsCompat.parse(date), dateFormats.ShortDate), attempts]
          )(completions);

          return ({
            content: {
              value: contentName,
            },
            completions: {
              value: completions.length,
            },
            options: {
              value: 
                <div>
                  {
                    completions.length > 0 ?
                      <Button
                        onClick={() => exportContentCompletions(contentName, data)}>
                        <Icon iconSet='glyphicon' icon='download-alt' /> export to csv
                      </Button> : null
                  }
                </div>,
            },
          });
        }
      )(contentCompletions);

      return {
        hasMoreToLoad,
        rows
      };
    }
  ),
  lifecycle({
    componentDidMount() {
      this.props.searchContent()
    },
    componentDidUpdate(prevProps) {
      const {
        query,
        searchContent,
        searchDebounce,
        setSearchDebounce,
        filter,
        setFilter,
      } = this.props

      if (query !== prevProps.query) {
        setFilter({
          ...filter,
          skip: 0,
          tags: query,
        });
      }

      if (filter !== prevProps.filter) {
        const debounced = fp.debounce(500)(() => {
          searchContent()
        });
        searchDebounce && searchDebounce.cancel();
        setSearchDebounce(debounced)
        debounced();
      }
    }
  })
)(ContentCompletionAdminDashboard);



// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/content-portal/content-completion.js